import { type FC, Suspense } from 'react';
import { graphql, useFragment, useLazyLoadQuery } from 'react-relay';
import FirmDetails from '../FirmDetails';

import { type PhotosSearchBrowseFirmDetailsRenderer_searchBrowsePhoto$key } from './__generated__/PhotosSearchBrowseFirmDetailsRenderer_searchBrowsePhoto.graphql';
import { type PhotosSearchBrowseFirmDetailsRendererQuery } from './__generated__/PhotosSearchBrowseFirmDetailsRendererQuery.graphql';

const FirmDetailsWrapper: FC<{
    firmId: string;
    firmName: string;
    profileLink: string;
    projectLink: string;
    projectName: string;
    caption: string;
}> = ({ firmId, firmName, projectName, profileLink, projectLink, caption }) => {
    const data = useLazyLoadQuery<PhotosSearchBrowseFirmDetailsRendererQuery>(
        graphql`
            query PhotosSearchBrowseFirmDetailsRendererQuery($firmId: Int!) {
                viewer {
                    tradeFirm(id: $firmId) {
                        publicProfile {
                            profilePhoto
                        }
                    }
                }
            }
        `,
        { firmId: Number(firmId) }
    );

    // fetching `publicProfile` separately b/c it is too expensive to fetch it in main query
    const tradeFirm = data.viewer?.tradeFirm || null;
    const firmProfile = tradeFirm?.publicProfile;
    const firmProfileImage = firmProfile?.profilePhoto || '';

    return (
        <FirmDetails
            firmName={firmName}
            projectName={projectName}
            firmProfileImage={firmProfileImage}
            profileLink={profileLink}
            projectLink={projectLink}
            caption={caption}
            isLoading={false}
        />
    );
};

type Props = {
    searchBrowsePhoto:
        | PhotosSearchBrowseFirmDetailsRenderer_searchBrowsePhoto$key
        | null
        | undefined;
};

const PhotosSearchBrowseFirmDetailsRenderer: FC<Props> = ({
    searchBrowsePhoto: searchBrowsePhotoRef,
}) => {
    const searchBrowsePhoto = useFragment(
        graphql`
            fragment PhotosSearchBrowseFirmDetailsRenderer_searchBrowsePhoto on PhotosSearchBrowsePhoto {
                caption
                designFirm {
                    serviceId
                    name
                    urlLabel
                }
                project {
                    name
                    url
                }
            }
        `,
        searchBrowsePhotoRef
    );
    const designFirm = searchBrowsePhoto?.designFirm;
    const firmId = designFirm?.serviceId;
    const urlLabel = designFirm?.urlLabel;
    const profileLink = urlLabel ? `/design-firms/${urlLabel}/` : '/design-firms/';
    const firmName = designFirm?.name || '';
    const caption = searchBrowsePhoto?.caption || '';
    const project = searchBrowsePhoto?.project;
    const projectLink = project?.url || '';
    const projectName = project?.name || '';

    if (!firmId) {
        return null;
    }

    return (
        <Suspense
            fallback={
                <FirmDetails
                    firmName={firmName}
                    projectName={projectName}
                    firmProfileImage={''}
                    profileLink={profileLink}
                    projectLink={projectLink}
                    caption={caption}
                    isLoading
                />
            }
        >
            <FirmDetailsWrapper
                firmId={firmId}
                firmName={firmName}
                projectName={projectName}
                profileLink={profileLink}
                projectLink={projectLink}
                caption={caption}
            />
        </Suspense>
    );
};

export default PhotosSearchBrowseFirmDetailsRenderer;
